import type { SponsorModule } from '@racemap/sdk/schema/events';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { shuffleArray } from '@racemap/utilities/functions';
import { useEffect, useMemo, useRef, useState } from 'react';

export const ROTATION_INTERVAL_MS = OneMinuteInMillis;

export interface LogoBox {
  maxWidth: number;
  maxHeight: number;
}

export interface NaturalSize {
  width: number;
  height: number;
}

/** Width an image renders at inside a contain-box, matching `object-fit: contain`. */
export function renderedWidth(size: NaturalSize, box: LogoBox): number {
  if (size.width <= 0 || size.height <= 0) return 0;
  return Math.min(box.maxWidth, box.maxHeight * (size.width / size.height));
}

/**
 * Longest prefix of `order` that fits into `available`.
 * The result is contiguous by design: the rotation cursor advances by the window
 * length, so a window that skipped an item would leave it behind forever.
 */
export function fitPrefix<T>(
  order: ReadonlyArray<T>,
  widthOf: (item: T) => number,
  gap: number,
  available: number,
): Array<T> {
  if (available <= 0) return [];

  const window: Array<T> = [];
  let usedWidth = 0;

  for (const item of order) {
    const needed = window.length > 0 ? widthOf(item) + gap : widthOf(item);
    if (usedWidth + needed > available) break;
    window.push(item);
    usedWidth += needed;
  }

  // A logo wider than the container is shown clipped rather than skipped, so the
  // cursor always advances instead of stalling on an empty window.
  if (window.length === 0 && order.length > 0) return [order[0]];

  return window;
}

interface LogoLike {
  id: string;
  imageUrls?: { logo?: string } | null;
  pinned?: boolean;
}

export const UNMEASURED: NaturalSize = { width: 0, height: 0 };

/**
 * Orders a window so the logos that matter most sit where the row is anchored: pinned
 * first, then the widest. Left-aligned rows lead with them, right-aligned rows end with
 * them, and the centred alignments grow outwards from the middle.
 */
export function arrangeByAlignment<T extends LogoLike>(
  logos: ReadonlyArray<T>,
  widthOf: (logo: T) => number,
  alignment: SponsorModule['alignment'],
): Array<T> {
  // A pinned logo outranks every width. The rank is finite on purpose: two `Infinity`
  // ranks would make the comparator return NaN.
  const rank = (logo: T) => (logo.pinned ? 1e6 : widthOf(logo));
  const byRank = [...logos].sort((a, b) => rank(b) - rank(a));

  if (alignment === 'left') return byRank;
  if (alignment === 'right') return byRank.reverse();

  // Grows outwards from both ends, so the highest rank ends up in the middle.
  const middleOut: Array<T> = [];
  byRank.forEach((logo, index) =>
    index % 2 === 1 ? middleOut.push(logo) : middleOut.unshift(logo),
  );
  return middleOut;
}

/**
 * Identity of a logo list by content rather than by array identity — the store
 * hands back a fresh array on every render. Order-insensitive, so reordering the
 * same logos does not count as a change; url-sensitive, so a re-uploaded logo does.
 * Pin-sensitive, so toggling the flag in the editor re-partitions the list.
 */
function contentKey<T extends LogoLike>(logos: ReadonlyArray<T> | null | undefined): string {
  return (
    logos
      ?.map((logo) => `${logo.id}:${logo.imageUrls?.logo ?? ''}:${logo.pinned ? 'pinned' : ''}`)
      .sort()
      .join(',') ?? ''
  );
}

/**
 * Whether measurement is still outstanding for any logo.
 *
 * An unmeasured logo has width 0, which `fitPrefix` would read as free and pack
 * into the window. Callers must hold off until this is false. A logo whose image
 * failed to load counts as settled — one broken URL must not hide the whole row.
 */
export function sizesPending<T extends LogoLike>(
  logos: ReadonlyArray<T> | null | undefined,
  sizes: Record<string, NaturalSize>,
): boolean {
  if (logos == null || logos.length === 0) return false;
  return logos.some((logo) => sizes[logo.id] == null);
}

/** Natural sizes of the logo images, keyed by logo id. */
export function useNaturalSizes<T extends LogoLike>(
  logos: ReadonlyArray<T> | null | undefined,
): Record<string, NaturalSize> {
  const [sizes, setSizes] = useState<Record<string, NaturalSize>>({});

  const logoKey = contentKey(logos);

  useEffect(() => {
    if (logos == null || logos.length === 0) {
      setSizes({});
      return;
    }

    let cancelled = false;
    Promise.all(
      logos.map(async (logo) => [logo.id, await getNaturalSize(logo.imageUrls?.logo)] as const),
    ).then((measured) => {
      if (!cancelled) setSizes(Object.fromEntries(measured));
    });

    return () => {
      cancelled = true;
    };
  }, [logoKey]);

  return sizes;
}

function getNaturalSize(src: string | undefined): Promise<NaturalSize> {
  if (src == null) return Promise.resolve(UNMEASURED);

  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve(UNMEASURED);
    image.src = src;
  });
}

/**
 * A window onto `logos` that walks the whole list, one fitting slice per
 * ROTATION_INTERVAL_MS. Pinned logos lead every window and are never rotated out;
 * the rest are shuffled once per logo set, then the cursor advances by the number of
 * rotating logos that fit, so consecutive windows tile them and every one is shown
 * once per cycle. `fit` must be memoised by the caller.
 */
export function useRotatingWindow<T extends LogoLike>(
  logos: ReadonlyArray<T> | null | undefined,
  fit: (order: ReadonlyArray<T>) => Array<T>,
): Array<T> {
  const logoKey = contentKey(logos);
  const pinned = useMemo(() => (logos ?? []).filter((logo) => logo.pinned), [logoKey]);
  const rotating = useMemo(
    () => shuffleArray((logos ?? []).filter((logo) => !logo.pinned)),
    [logoKey],
  );

  const [cursor, setCursor] = useState(0);
  useEffect(() => setCursor(0), [logoKey]);

  // Pinned logos lead the order, so `fitPrefix` reserves their width before it packs
  // any rotating one — no separate width accounting needed.
  const window = useMemo(
    () => fit([...pinned, ...rotate(rotating, cursor % (rotating.length || 1))]),
    [pinned, rotating, cursor, fit],
  );

  // How many rotating logos made it into this window, and therefore how far the cursor
  // moves. Pinned logos that fill the row on their own leave this at 0: rotation stops
  // and only they are shown — the editor warns above two pinned logos.
  const step = Math.max(0, window.length - pinned.length);
  const stepRef = useRef(step);
  stepRef.current = step;

  // Nothing to rotate while every logo is on screen. Depending on the step
  // rather than the window itself keeps a resize from restarting the interval.
  const rotates = step > 0 && step < rotating.length;

  useEffect(() => {
    if (!rotates) return;
    const interval = setInterval(
      () => setCursor((current) => (current + stepRef.current) % rotating.length),
      ROTATION_INTERVAL_MS,
    );
    return () => clearInterval(interval);
  }, [rotates, rotating.length]);

  return window;
}

function rotate<T>(items: ReadonlyArray<T>, by: number): Array<T> {
  if (by === 0) return items.slice();
  return [...items.slice(by), ...items.slice(0, by)];
}
